import type { DocumentKind } from '@crm/contracts';
import { api, type CandidateDocument } from './client.ts';

/** Hex SHA-256 of a file's bytes, computed in the browser so `:complete` can store it. */
export async function sha256Hex(file: Blob): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', await bytesOf(file));
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

/** `Blob.arrayBuffer()` where it exists; FileReader where it does not (jsdom). */
function bytesOf(file: Blob): Promise<ArrayBuffer> {
  if (typeof file.arrayBuffer === 'function') return file.arrayBuffer();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = () => reject(reader.error ?? new Error('Could not read the file'));
    reader.readAsArrayBuffer(file);
  });
}

/**
 * The upload flow (RELAY-943): init → PUT the bytes to the presigned URL → complete. The API
 * only makes the row a document, and the candidate's resume, once S3 confirms the bytes.
 * The PUT sends exactly the `uploadHeaders` init returned: they are signed (the bucket's KMS
 * key among them), so S3 refuses the upload without them.
 */
export async function uploadCandidateDocument(
  candidateId: string,
  file: File,
  kind: DocumentKind = 'resume',
): Promise<CandidateDocument> {
  const contentType = file.type || 'application/octet-stream';
  const { document, uploadUrl, uploadHeaders } = await api.candidates.documentsInit(candidateId, {
    filename: file.name,
    contentType,
    sizeBytes: file.size,
    kind,
  });
  const put = await fetch(uploadUrl, {
    method: 'PUT',
    body: file,
    headers: uploadHeaders,
  });
  if (!put.ok) throw new Error(`Upload failed (${put.status})`);
  return api.candidates.documentsComplete(candidateId, {
    documentId: document.id,
    sha256: await sha256Hex(file),
  });
}

/** Open a document in a new tab: inline, so a PDF renders in the browser. */
export async function openDocument(id: string): Promise<void> {
  const { downloadUrl } = await api.documents.downloadUrl(id, 'inline');
  window.open(downloadUrl, '_blank', 'noopener');
}

/** A PDF opens in the in-app viewer (TS-1896); anything else keeps opening in a tab. */
export function isPdf(doc: { contentType: string; filename: string }): boolean {
  return doc.contentType === 'application/pdf' || /\.pdf$/i.test(doc.filename);
}

/** Save a document: an attachment URL, so the browser downloads it rather than showing it. */
export async function downloadDocument(id: string): Promise<void> {
  const { downloadUrl } = await api.documents.downloadUrl(id, 'attachment');
  window.open(downloadUrl, '_blank', 'noopener');
}

/**
 * A document's bytes, for the PDF viewer. The presigned URL is the S3 bucket's, another origin
 * than the web app's, so this GET only works where the bucket's CORS rule allows GET.
 */
export async function fetchDocumentBytes(id: string): Promise<ArrayBuffer> {
  const { downloadUrl } = await api.documents.downloadUrl(id, 'inline');
  const res = await fetch(downloadUrl);
  if (!res.ok) throw new Error(`Could not load the file (${res.status})`);
  return res.arrayBuffer();
}
